import React, { useEffect, useMemo } from 'react'
import { useLocation, useParams } from 'react-router'
import { useIntl } from 'react-intl'

import Breadcrumbs from 'components/Breadcrumbs'
import { useGetCategoriesQuery } from 'containers/Categories/rtkApi'
import { retailTrackCategoryView } from 'services/analytics/retail'
import { useQueryFilters } from 'hooks'
import { ROUTES } from 'consts'

import { generatePathWithSearch, useCatalogTitle } from './utils'
import Title from './Title'
import messages from './messages'

const Header = ({ productLevel, className }) => {
  const { formatMessage } = useIntl()
  const { pathname } = useLocation()
  const params = useParams()
  const levelParam = params.subgroup || params.group || params.category

  const { data, isFetching } = useGetCategoriesQuery(
    {
      id: levelParam,
    },
    { skip: !levelParam },
  )
  const fetchedLevel = !levelParam ? undefined : data

  const level = productLevel || fetchedLevel
  const title = useCatalogTitle(level?.name)

  const { searchQuery = '' } = useQueryFilters()
  const path = pathname.startsWith(ROUTES.ALL_PRODUCTS_SEARCH_RESULTS)
    ? ROUTES.ALL_PRODUCTS_SEARCH_RESULTS
    : ROUTES.ALL_PRODUCTS

  const flattenedData = useMemo(
    () => {
      const result = []
      if (!level) return result

      const flatten = ({ id, name, parent }, route = []) => {
        if (parent) {
          flatten(parent, route)
        }
        route.push(id)
        result.push({
          id,
          name,
          route: generatePathWithSearch(`${path}/${route.join('/')}`, {
            search: searchQuery,
          }),
        })
      }

      flatten(level)
      return result
    },
    [level, searchQuery, path],
  )

  useEffect(
    () => {
      const [category, group, subgroup] = flattenedData
      if (!productLevel && category) {
        retailTrackCategoryView({
          categoryName: category.name,
          groupName: group?.name,
          subgroupName: subgroup?.name,
        })
      }
    },
    [flattenedData, productLevel],
  )

  const allBreadcrumbs = [
    {
      id: 'products_root',
      name: formatMessage(messages.allProducts),
      route: generatePathWithSearch(path, { search: searchQuery }),
    },
    ...flattenedData,
  ]

  const breadcrumbs = productLevel
    ? allBreadcrumbs
    : allBreadcrumbs.slice(0, -1)

  return (
    <div className="flex flex-col gap-8 md:gap-12">
      <Breadcrumbs
        breadcrumbs={isFetching ? [] : breadcrumbs}
        className={className}
      />
      {!productLevel && <Title title={title} />}
    </div>
  )
}

export default Header
